import { useState } from 'react';
import { toast } from 'react-toastify';

import type { WalletInterface } from '@api';

import Delete from '@uikit/icons/Delete';

import { ActionButton } from '@shared/common/components/Buttons/ActionButton';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import truncate from '@shared/helpers/web3/truncate';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';

import { useDeleteWalletMutation } from '@query/user';

import { CopyButton } from '@ui/uikit/components/common/CopyButton';

import { WalletConnectType } from '../model/walletsConfig';
import { WalletListItem } from '../WalletListItem';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';

interface Props {
  walletInfo: WalletInterface;
  connectType: WalletConnectType;
  withActionButtons?: boolean;
}

export const ConnectedWallet = ({ walletInfo, connectType, withActionButtons }: Props) => {
  const { mutateAsync: deleteWallet } = useDeleteWalletMutation();
  const featureToggles = useFeatureToggles();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const {
    attributes: { address, network },
  } = walletInfo;

  const handleDeleteWalletClick = async ({ id }: WalletInterface) => {
    if (id) {
      try {
        await deleteWallet(id);

        toast.success('Wallet successfully unassigned from MagicID');
      } catch (error) {
        displayErrorToast({ error });
      } finally {
        setIsDeleteModalOpen(false);
      }
    }
  };

  const isHumanityVerification =
    !!featureToggles.humanityVerification && connectType === WalletConnectType.EVM;

  return (
    <>
      <WalletListItem title={truncate(walletInfo.attributes.address)} connectType={connectType}>
        {withActionButtons && (
          <div className="flex gap-2">
            <ActionButton
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
              data-qa="profile-wallet-dalete-button"
            >
              <Delete className="size-4" />
            </ActionButton>
            <CopyButton
              value={address}
              className="msq-btn msq-btn-icon-lg msq-btn-secondary"
              data-qa="profile-wallet-copy-button"
            />
          </div>
        )}
      </WalletListItem>
      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onConfirm={() => handleDeleteWalletClick(walletInfo)}
        isHumanityVerification={isHumanityVerification}
        network={network}
        address={address}
      />
    </>
  );
};
